import { useTranslation } from 'react-i18next';
import type { IFormControlProps, ILovDetail, IProperty } from '../../../types';
import {
  EFieldSize,
  EMasterPropertyCodes,
  EPropertyType,
  ShowOptionCodeForProperties,
} from '../../data/Constants';
import { isRcidOptionPastDated } from '../../services/PropertyHelperFunctions';
import AutocompleteInput from '../InputComponents/AutocompleteInput';
import { MultiAutocompleteInput } from '../InputComponents/MultiAutocompleteInput';
import { NumericFieldInput } from '../InputComponents/NumericFieldInput';
import { TextFieldInput } from '../InputComponents/TextFieldInput';
import { ToggleSwitchInput } from '../InputComponents/ToggleSwitchInput';

/**
 * To render Enum property inputs in PDM tab
 * @param {IProperty} property is an object of product properties
 * @param {string | null} propValue is property value
 * @param {function} handleInputChange is a function to manage input change
 * @param {boolean} fieldsEnabled is a boolean flag
 * @param {function} t is an react function used for translation
 * @returns {React.JSX.Element} returns React element
 */

const getFormattedEnumValue = (
  property: IProperty,
  propValue: string | null
): ILovDetail | ILovDetail[] | null => {
  if (!property.listOfValues?.length || !propValue) return null;
  const val = JSON.parse(propValue);
  if (!val.length) return null;
  if (property.selectionType === EPropertyType.Multiple) {
    return val.map((valLov: string) => {
      const match = property.listOfValues?.find((lov) => lov.code === valLov);
      return match ?? { code: valLov, name: valLov, inValid: true }; // inValid property highlights invalid values in UI
    });
  }
  if (property.selectionType === EPropertyType.Single) {
    const match = property.listOfValues?.find((lov) => lov.code === val[0]);
    if (property.code === EMasterPropertyCodes.RCID && match && isRcidOptionPastDated(match)) {
      return { code: match.code, name: val[0], inValid: true };
    }
    return match ?? { code: val[0], name: val[0], inValid: true };
  }
  return null;
};

const renderEnumInput = (
  property: IProperty,
  propValue: string | null,
  handleInputChange: (code: string, value: string) => void,
  fieldsEnabled: boolean,
  t,
  i18n
): React.JSX.Element => {
  const formattedVal = getFormattedEnumValue(property, propValue);

  const fieldLabel = i18n.exists('labels.properties.' + property.code)
    ? t('labels.properties.' + property.code)
    : property.name + ' (' + property.code + ')';

  const isRequired =
    !!property.isRequired &&
    (Array.isArray(formattedVal) ? formattedVal.length === 0 : formattedVal === null);

  if (property.selectionType === EPropertyType.Multiple) {
    return (
      <MultiAutocompleteInput
        data={property.listOfValues || []}
        handleChange={handleInputChange}
        label={fieldLabel}
        disabled={!fieldsEnabled}
        value={formattedVal}
        code={property.code}
        disableClear={false}
        size={EFieldSize.Small}
        showCode={ShowOptionCodeForProperties.includes(property.code)}
        isRequired={isRequired}
      />
    );
  } else if (property.selectionType === EPropertyType.Single) {
    return (
      <AutocompleteInput
        data={property.listOfValues || []}
        handleChange={handleInputChange}
        label={fieldLabel}
        disabled={!fieldsEnabled}
        showCode={ShowOptionCodeForProperties.includes(property.code)}
        value={formattedVal}
        code={property.code}
        disableClear={false}
        size={EFieldSize.Small}
        isRequired={isRequired}
        getOptionDisabled={
          property.code === EMasterPropertyCodes.RCID ? isRcidOptionPastDated : undefined
        }
      />
    );
  }

  return <></>;
};

/**
 * returns form control based on property type
 * @param {IFormControlProps} props is used to send property details of product
 * @returns {React.JSX.Element} returns React element
 */

export const DynamicInputControl = (props: IFormControlProps): React.JSX.Element => {
  const { property, propValue, handleInputChange, fieldsEnabled } = props;
  const { t, i18n } = useTranslation();
  const isRequired = !!property.isRequired && !propValue;

  switch (property.type) {
    case EPropertyType.Boolean:
      return (
        <ToggleSwitchInput
          label={
            i18n.exists('labels.properties.' + property.code)
              ? t('labels.properties.' + property.code)
              : property.name + ' (' + property.code + ')'
          }
          disabled={!fieldsEnabled}
          handleInputChange={handleInputChange}
          value={propValue}
          code={property.code}
          size={EFieldSize.Medium}
        />
      );
    case EPropertyType.String:
      return (
        <TextFieldInput
          label={
            i18n.exists('labels.properties.' + property.code)
              ? t('labels.properties.' + property.code)
              : property.name + ' (' + property.code + ')'
          }
          handleInputChange={handleInputChange}
          disabled={!fieldsEnabled}
          value={propValue}
          code={property.code}
          size={EFieldSize.Small}
          isRequired={isRequired}
        />
      );
    case EPropertyType.Numeric:
      return (
        <NumericFieldInput
          label={
            i18n.exists('labels.properties.' + property.code)
              ? t('labels.properties.' + property.code)
              : property.name + ' (' + property.code + ')'
          }
          handleInputChange={handleInputChange}
          disabled={!fieldsEnabled}
          value={propValue}
          code={property.code}
          size={EFieldSize.Small}
          isRequired={isRequired}
        />
      );
    case EPropertyType.Enum:
      return renderEnumInput(property, propValue, handleInputChange, fieldsEnabled, t, i18n);
    default:
      return <></>;
  }
};
